import type { Verification } from "@sitescene/types";

import { useState } from "react";
import { useNavigate } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { plausibilityVerificationPath } from "@/constants/routes";
import {
	useVerifications,
	type VerificationFilters,
} from "@/features/plausibility/use-verifications";

import { formatPercent, formatSeverityChange, formatWhen } from "./format";

const DECISIONS = ["alertNow", "escalate", "reviewQueue", "lowPriorityLog"];
const STATUSES = ["completed", "skipped", "failed", "pending"];

/**
 * What the verifier did with each alert-worthy detection. In shadow mode this
 * is the whole product: a reviewer reads the decision the verifier *would*
 * have made and compares it with what the clip actually showed.
 */
export const Verifications = () => {
	const [filters, setFilters] = useState<VerificationFilters>({});
	const navigate = useNavigate();
	const query = useVerifications(filters);
	const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

	const columns: Column<Verification>[] = [
		{
			header: "Captured",
			key: "captured",
			render: (row) => formatWhen(row.input.capturedAt),
		},
		{
			header: "Stage 1",
			key: "stage1",
			render: (row) => (
				<span className="font-mono text-xs">{row.input.type}</span>
			),
		},
		{
			header: "Class",
			key: "class",
			render: (row) => row.classId ?? "—",
		},
		{
			header: "Severity",
			key: "severity",
			render: (row) =>
				formatSeverityChange(row.input.severity, row.proposedSeverity),
		},
		{
			header: "Posterior",
			key: "posterior",
			render: (row) => formatPercent(row.posterior),
		},
		{
			header: "Decision",
			key: "decision",
			render: (row) => <Badge value={row.decision} />,
		},
		{
			header: "Verdict",
			key: "verdict",
			render: (row) =>
				row.verdict ? (
					<span className="text-xs">
						{row.verdict.trueClassId === row.classId ? "✓ " : "✗ "}
						{row.verdict.trueClassId}
					</span>
				) : row.status === "completed" ? (
					<span className="text-xs text-tertiary">needs verdict</span>
				) : (
					<span className="text-tertiary">—</span>
				),
		},
		{
			header: "Status",
			key: "status",
			render: (row) => (
				<span>
					<Badge value={row.status} />
					{row.skipReason && (
						<span className="ml-1 text-xs text-tertiary">{row.skipReason}</span>
					)}
				</span>
			),
		},
	];

	return (
		<div>
			<form
				className="flex flex-wrap items-end gap-3"
				onSubmit={(event) => event.preventDefault()}
			>
				<NativeSelect
					label="Status"
					onChange={(e) =>
						setFilters((f) => ({ ...f, status: e.target.value || undefined }))
					}
					options={[
						{ label: "Any", value: "" },
						...STATUSES.map((s) => ({ label: s, value: s })),
					]}
					value={filters.status ?? ""}
				/>
				<NativeSelect
					label="Decision"
					onChange={(e) =>
						setFilters((f) => ({
							...f,
							decision: e.target.value || undefined,
						}))
					}
					options={[
						{ label: "Any", value: "" },
						...DECISIONS.map((d) => ({ label: d, value: d })),
					]}
					value={filters.decision ?? ""}
				/>
				<label
					className="flex items-center gap-2 pb-1 text-sm"
					htmlFor="filter-needs-verdict"
				>
					<input
						checked={filters.needsVerdict === "true"}
						id="filter-needs-verdict"
						onChange={(e) =>
							setFilters((f) => ({
								...f,
								needsVerdict: e.target.checked ? "true" : undefined,
							}))
						}
						type="checkbox"
					/>
					<span className="text-secondary">Needs verdict</span>
				</label>
				<Input
					className="w-auto"
					label="Class id"
					onChange={(classId) =>
						setFilters((f) => ({
							...f,
							classId: classId || undefined,
						}))
					}
					placeholder="animal.wildlife"
					value={filters.classId ?? ""}
				/>
			</form>

			<div className="mt-4">
				<StatusMessage
					empty="No verifications yet for this organization. Set the mode to shadow and let alert-worthy detections flow."
					error={query.error}
					isEmpty={!query.isLoading && rows.length === 0}
					isLoading={query.isLoading}
					loading="Loading verifications…"
				/>
				{rows.length > 0 && (
					<>
						<DataTable
							columns={columns}
							onRowClick={(row) =>
								void navigate(plausibilityVerificationPath(row.id))
							}
							rowKey={(row) => row.id}
							rows={rows}
						/>
						{query.hasNextPage && (
							<div className="mt-3">
								<Button
									color="secondary"
									isDisabled={query.isFetchingNextPage}
									onClick={() => void query.fetchNextPage()}
									size="sm"
									type="button"
								>
									{query.isFetchingNextPage ? "Loading…" : "Load more"}
								</Button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};
